import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import BackToTop from "@/components/BackToTop"
import Analytics from "@/components/Analytics"
import VerificationMeta from "@/components/VerificationMeta"
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/components/Seo"

const APPLE_MAPS_LAT  = 53.044
const APPLE_MAPS_LON  = -2.181
const APPLE_MAPS_NAME = "Pathway Academy Zone"

const SOCIAL_LINKS = [
  "https://www.facebook.com/PathwayAcademyZone",
  "https://www.linkedin.com/company/pathway-academy-zone",
  "https://twitter.com/PathwayAcademyZ",
]

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE, width: 512, height: 512 },
      sameAs: SOCIAL_LINKS,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+44 1782 365365",
        contactType: "customer service",
        email: "info@pathwayacademyzone.co.uk",
        areaServed: "GB",
        availableLanguage: ["English"],
      },
    },
    {
      "@type": ["EducationalOrganization", "LocalBusiness"],
      "@id": `${SITE_URL}#localbusiness`,
      name: SITE_NAME,
      url: SITE_URL,
      image: DEFAULT_OG_IMAGE,
      telephone: "+44 1782 365365",
      email: "info@pathwayacademyzone.co.uk",
      priceRange: "Free for referred learners",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Duncalf Street, Burslem",
        addressLocality: "Stoke-on-Trent",
        addressRegion: "Staffordshire",
        postalCode: "ST6 3LJ",
        addressCountry: "GB",
      },
      geo: { "@type": "GeoCoordinates", latitude: 53.044, longitude: -2.181 },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:30",
          closes: "16:00",
        },
      ],
      areaServed: [
        { "@type": "City", name: "Stoke-on-Trent" },
        { "@type": "AdministrativeArea", name: "Staffordshire" },
      ],
      sameAs: SOCIAL_LINKS,
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}#website`,
      url: SITE_URL,
      name: SITE_NAME,
      inLanguage: "en-GB",
      publisher: { "@id": `${SITE_URL}#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    },
  ],
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()

  // Inject the global Organization / LocalBusiness / WebSite graph once on mount.
  useEffect(() => {
    const id = "global-org"
    const existing = document.head.querySelector(`script[data-seo="${id}"]`)
    if (existing) existing.remove()
    const script = document.createElement("script")
    script.type = "application/ld+json"
    script.setAttribute("data-seo", id)
    script.text = JSON.stringify(ORGANIZATION_LD)
    document.head.appendChild(script)
  }, [])

  // Inject RSS autodiscovery + Apple Maps business meta once.
  useEffect(() => {
    // RSS feed autodiscovery link
    if (!document.head.querySelector('link[rel="alternate"][type="application/rss+xml"]')) {
      const rss = document.createElement("link")
      rss.rel = "alternate"
      rss.type = "application/rss+xml"
      rss.title = `${SITE_NAME} Blog`
      rss.href = `${SITE_URL}/feed.xml`
      document.head.appendChild(rss)
    }
    // Apple Maps: geo meta for business listing enrichment
    // (complements the schema.org geo already in ORGANIZATION_LD)
    const geoMetas: [string, string][] = [
      ["geo.region",    "GB-STE"],
      ["geo.placename", "Stoke-on-Trent, Staffordshire"],
      ["geo.position",  `${APPLE_MAPS_LAT};${APPLE_MAPS_LON}`],
      ["ICBM",          `${APPLE_MAPS_LAT}, ${APPLE_MAPS_LON}`],
    ]
    for (const [name, content] of geoMetas) {
      if (!document.head.querySelector(`meta[name="${name}"]`)) {
        const m = document.createElement("meta")
        m.name = name; m.content = content
        document.head.appendChild(m)
      }
    }
    // Apple Maps deep-link (opens the business in Apple Maps on iOS/macOS)
    if (!document.head.querySelector('meta[name="apple-maps-record"]')) {
      const apm = document.createElement("meta")
      apm.name = "apple-maps-record"
      apm.content = encodeURIComponent(
        `maps://?ll=${APPLE_MAPS_LAT},${APPLE_MAPS_LON}&q=${encodeURIComponent(APPLE_MAPS_NAME)}`
      )
      document.head.appendChild(apm)
    }
  }, [])

  // Restore scroll on route change.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary focus:text-primary-foreground focus:font-semibold"
      >
        Skip to main content
      </a>
      <VerificationMeta />
      <Analytics />
      <Header />
      <main id="main" className="flex-1 pt-20" tabIndex={-1}>
        {children}
      </main>
      <Footer />
      <BackToTop />
    </div>
  )
}
